import { useCallback, useEffect, useRef, useState } from "react";
import { recordsApi } from "../../api";
import { MISSION_ACTIVITY_EVENT } from "../../missionEvents";
export type ReviewPeriod = "week" | "month";
export interface PeriodTotals {
  start: string;
  through: string;
  total: number;
  active_days: number;
  covered_dimensions: number;
  by_type: Record<string, number>;
  dimensions: Record<string, number>;
  exercise_minutes: number;
  water_cups: number;
  study_events: number;
}
export interface RecordInsightsData {
  period: ReviewPeriod;
  today: string;
  selection: {
    start: string;
    end: string;
    through: string;
    is_current: boolean;
    previous_anchor: string;
    next_anchor: string | null;
  };
  current: PeriodTotals;
  comparison: PeriodTotals;
  partial: boolean;
  comparison_is_shorter: boolean;
  trend: Array<PeriodTotals & { end: string }>;
  dimension_labels: Record<string, string>;
  type_labels: Record<string, string>;
}
export function beijingToday(): string {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  return ["year", "month", "day"]
    .map((key) => parts.find((part) => part.type === key)?.value)
    .join("-");
}
export function useRecordInsights(period: ReviewPeriod, anchor: string) {
  const generation = useRef(0);
  const [data, setData] = useState<RecordInsightsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const reload = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setError("");
    setData(null);
    try {
      const response = await recordsApi.summary({ period, anchor });
      if (request === generation.current) setData(response.data);
    } catch (e: any) {
      if (request === generation.current)
        setError(e?.response?.data?.detail || "汇总加载失败，请重试");
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [period, anchor]);
  useEffect(() => {
    const requests = generation;
    let timer: ReturnType<typeof setTimeout>;
    reload();
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(reload, 150);
    };
    window.addEventListener(MISSION_ACTIVITY_EVENT, refresh);
    return () => {
      requests.current++;
      clearTimeout(timer);
      window.removeEventListener(MISSION_ACTIVITY_EVENT, refresh);
    };
  }, [reload]);
  return { data, loading, error, reload };
}
